import React, { useEffect, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Star, FileText } from 'lucide-react';
import Navbar from '@/components/Navbar';
import { useLang } from '@/contexts/LanguageContext';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ReceivedReviews() {
  const { t } = useLang();
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/restaurant/received-reviews`, { withCredentials: true });
        setReviews(res.data || []);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    })();
  }, []);

  if (loading) return <div className="min-h-screen bg-background"><Navbar /><div className="flex items-center justify-center h-[60vh]"><div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div></div>;

  const avg = reviews.length > 0 ? (reviews.reduce((s, r) => s + (r.rating || 0), 0) / reviews.length).toFixed(1) : '—';

  return (
    <div className="min-h-screen bg-background pb-20 md:pb-8" data-testid="received-reviews-page">
      <Navbar />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 sm:py-8 pt-20 sm:pt-24">
        <div className="mb-6 sm:mb-8">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground mb-2 font-sans">{t('nav_my_restaurant')}</p>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{t('rr_title')}</h1>
          <p className="text-muted-foreground text-sm mt-1 font-sans">{t('rr_subtitle')}</p>
        </div>

        {/* KPI Summary */}
        <Card className="border border-border rounded-2xl mb-6" data-testid="received-reviews-summary">
          <CardContent className="p-5 sm:p-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <div>
              <p className="text-xs text-muted-foreground font-sans">{t('rr_total')}</p>
              <p className="text-3xl font-bold" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{reviews.length}</p>
            </div>
            <div className="text-left sm:text-right">
              <p className="text-xs text-muted-foreground font-sans">{t('rr_avg')}</p>
              <div className="flex items-center gap-2">
                <p className="text-3xl font-bold" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{avg}</p>
                <Star className="w-6 h-6 fill-amber-400 text-amber-400" strokeWidth={1.5} />
              </div>
            </div>
          </CardContent>
        </Card>

        {reviews.length === 0 ? (
          <Card className="border border-border rounded-2xl"><CardContent className="p-8 text-center">
            <FileText className="w-10 h-10 text-muted-foreground mx-auto mb-3" strokeWidth={1.5} />
            <p className="text-muted-foreground text-sm font-sans">{t('rr_empty')}</p>
          </CardContent></Card>
        ) : (
          <div className="space-y-3 sm:space-y-4">
            {reviews.map((rv, i) => (
              <Card key={rv.review_id} className="border border-border rounded-2xl card-hover" data-testid={`received-review-${i}`}>
                <CardContent className="p-4 sm:p-6">
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <div>
                      <p className="font-semibold text-sm font-sans">{rv.reviewer_name}</p>
                      <p className="text-xs text-muted-foreground font-sans">{new Date(rv.created_at).toLocaleDateString()}</p>
                    </div>
                    {rv.rating > 0 && (
                      <div className="flex items-center gap-0.5" data-testid={`received-review-rating-${i}`}>
                        {[1,2,3,4,5].map(s => <Star key={s} className={`w-4 h-4 ${s <= rv.rating ? 'fill-amber-400 text-amber-400' : 'text-border'}`} strokeWidth={1.5} />)}
                      </div>
                    )}
                  </div>
                  {rv.text && <p className="text-sm text-foreground font-sans leading-relaxed" data-testid={`received-review-text-${i}`}>{rv.text}</p>}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
